'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { ChevronDown, ChevronUp, AlertTriangle, Shield, X } from 'lucide-react'
import { useTranslation } from '@/i18n/client'

interface MapDisclaimerProps {
  onAccept: () => void
  type?: '2d' | '3d'
}

// KEIN localStorage, KEIN Cookie — Disclaimer wird IMMER bei jedem Aufruf angezeigt.
// Rechtlich erforderlich: Jede Nutzung der Karte erfordert erneute Bestätigung.

export function MapDisclaimer({ onAccept, type = '3d' }: MapDisclaimerProps) {
  const { t } = useTranslation()
  const router = useRouter()
  // Accordion default ZUGEKLAPPT (2026-05-08 User-Wunsch).
  // Rechtlich OK: Kurzversion + Pilotenpflicht-Kernpunkte stehen oben sichtbar,
  // Volltext bleibt 1-Klick erreichbar. Informierte Einwilligung weiterhin
  // gegeben — User muss die Checkbox aktiv anhaken.
  const [accordionOpen, setAccordionOpen] = useState(false)
  const [checkboxChecked, setCheckboxChecked] = useState(false)

  const handleAccept = () => {
    if (!checkboxChecked) return
    onAccept()
  }

  // 2026-05-20: X-Button schließt das Modal → springt zurück zur vorherigen
  // Seite. Wenn kein History-Eintrag da ist (Direkt-Link), Fallback auf '/'.
  const handleClose = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back()
    } else {
      router.push('/')
    }
  }

  // ESC schließt ebenfalls (Standard-Modal-Verhalten)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const mapType = type === '3d' ? t('mapDisclaimer.type3d') : t('mapDisclaimer.type2d')

  return (
    // R100.57 (2026-05-30): Overlay startet UNTER dem Header (top-[120px]) damit
    // Stephans Bug-Report behoben ist — Menü oben bleibt sichtbar + nutzbar.
    // Compliance bleibt: ohne Accept kein Map-Zugriff. Backdrop deckt nur den
    // Page-Content unter dem Header ab. Modal ist auch kompakter (max-h-[80vh])
    // damit es nicht so dominant wirkt.
    <div
      className="fixed top-[120px] right-0 bottom-0 left-0 lg:left-[var(--sidebar-w,0)] z-[180] flex items-start justify-center p-4 pt-8 transition-[left] duration-[420ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{ background: 'var(--modal-overlay, rgba(0,0,0,0.65))', backdropFilter: 'blur(4px)' }}
    >
      <div className="relative w-full max-w-3xl max-h-[80vh] overflow-y-auto rounded-[20px] p-5 sm:p-7" style={{ background: 'var(--modal-bg, var(--bg-card))', border: '1px solid var(--modal-bdr, var(--border))' }}>

        {/* X-Close-Button oben rechts — springt zurück zur vorigen Seite */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Schließen — zurück zur vorigen Seite"
          title="Schließen (Esc)"
          className="absolute top-4 right-4 inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors hover:bg-[var(--bg-secondary)]"
          style={{ color: 'var(--muted)' }}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-start gap-4 mb-6 pr-12">
          <div className="shrink-0 p-3 rounded-xl" style={{ background: 'rgba(234,179,8,0.1)' }}>
            <AlertTriangle className="h-8 w-8" style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h2 className="heading-lg" style={{ color: 'var(--accent)' }}>
              {t('mapDisclaimer.title')}
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>{t('mapDisclaimer.readRequired')}</p>
          </div>
        </div>

        <div className="rounded-xl p-4 mb-4" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>
            {t('mapDisclaimer.shortVersion', { mapType })}
          </p>
          <p className="text-sm leading-relaxed mt-3" style={{ color: 'var(--text)' }}>
            {t('mapDisclaimer.userConfirm')}
          </p>
        </div>

        <div className="rounded-xl overflow-hidden mb-6" style={{ border: '1px solid var(--border)' }}>
          <button
            onClick={() => setAccordionOpen(!accordionOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-start text-sm font-bold transition-colors"
            style={{ background: 'var(--bg-secondary)', color: 'var(--text)' }}
          >
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" style={{ color: 'var(--accent)' }} />
              <span>{t('mapDisclaimer.fullDisclaimer')}</span>
            </div>
            {accordionOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {accordionOpen && (
            <div className="px-4 py-4 text-xs leading-relaxed space-y-3" style={{ color: 'var(--muted)', background: 'var(--bg-primary)' }}>
              <p>{t('mapDisclaimer.fullPara1', { mapType })}</p>
              <p>{t('mapDisclaimer.fullPara2')}</p>
              <p>{t('mapDisclaimer.fullPara3')}</p>
              <p>{t('mapDisclaimer.fullPara4')}</p>
              <p>{t('mapDisclaimer.fullPara5')}</p>
              <p>{t('mapDisclaimer.fullPara6')}</p>
              {t('mapDisclaimer.fullPara7') !== 'mapDisclaimer.fullPara7' && (
                <p>{t('mapDisclaimer.fullPara7')}</p>
              )}
              <p className="font-bold" style={{ color: 'var(--accent)' }}>
                {t('mapDisclaimer.pilotObligation')}
              </p>
              <ul className="list-disc ps-5 space-y-1">
                <li>{t('mapDisclaimer.pilotObl1')}</li>
                <li>{t('mapDisclaimer.pilotObl2')}</li>
                <li>{t('mapDisclaimer.pilotObl3')}</li>
              </ul>
              <p className="font-medium" style={{ color: 'var(--text)' }}>
                {t('mapDisclaimer.userAcknowledges')}
              </p>
              <ul className="list-disc ps-5 space-y-1">
                <li>{t('mapDisclaimer.ack1')}</li>
                <li>{t('mapDisclaimer.ack2')}</li>
                <li>{t('mapDisclaimer.ack3')}</li>
                <li>{t('mapDisclaimer.ack4')}</li>
                <li><strong>{t('mapDisclaimer.ack5')}</strong></li>
                <li><strong style={{ color: '#ef4444' }}>{t('mapDisclaimer.ack6')}</strong></li>
              </ul>
            </div>
          )}
        </div>

        <label className="flex items-start gap-3 mb-5 p-4 rounded-xl cursor-pointer" style={{ background: 'var(--bg-secondary)', border: `1px solid ${checkboxChecked ? 'var(--accent)' : 'var(--border)'}` }}>
          <input
            type="checkbox"
            checked={checkboxChecked}
            onChange={(e) => setCheckboxChecked(e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-yellow-500"
          />
          <span className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>
            {t('mapDisclaimer.checkboxLabel')}
          </span>
        </label>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleAccept}
            disabled={!checkboxChecked}
            className="btn-primary flex-1 py-3 text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t('mapDisclaimer.acceptButton')}
          </button>
          <a href="/" className="btn-secondary flex-1 py-3 text-sm font-bold text-center">
            {t('mapDisclaimer.backButton')}
          </a>
        </div>

        <p className="text-center text-xs mt-4" style={{ color: 'var(--muted)' }}>
          Dieser Hinweis wird bei jedem Aufruf der Karte angezeigt und muss jedes Mal bestätigt werden.
        </p>
      </div>
    </div>
  )
}
